import {SvgIcon} from "@mui/material";
import {
  AdjustOutlined,
  BorderStyleOutlined,
  BrightnessLowOutlined,
  CenterFocusStrongOutlined,
  ChangeHistoryOutlined,
  CircleOutlined,
  ContrastOutlined,
  ControlCameraOutlined, GradientOutlined,
  InsertCommentOutlined,
  InvertColorsOutlined,
  LabelOutlined,
  PanToolOutlined,
  RectangleOutlined,
  RotateRightOutlined,
  StraightenOutlined
} from "@mui/icons-material";
import {$KTM, KeyMetadatas} from "@/shared/types";
import {ReactElement} from "react";
import {
  LabelerSegmentation,
  LabelerSegmentationType,
  LabelerTask,
  LabelerTaskCase,
  LabelerTaskCaseLabel,
  LabelerTaskCategory
} from "@/fyneai-shared/types";

export enum Shape {
  POINT = "point",
  RECTANGLE = "rectangle",
  ELLIPSE = "ellipse",
  POLYGON = "polygon",
  COMMENT = "comment",
}

export type CommentElement = {
  id: string;
  x: number;
  y: number;
  text: string;
  author?: string;
  timestamp: number;
}

export interface SidebarContext {
  case_?: LabelerTaskCase;
  elements?: ShapeCanvasElement[];
  comments?: CommentElement[];
  onSelectElement?: (id: string) => void;
  onDeleteElement?: (id: string) => void;
  onAddComment?: (comment: CommentElement) => void;
  onDeleteComment?: (id: string) => void;
  onSelectComment?: (id: string) => void;
}

export interface SidebarRenderer {
  renderToolbar(context?: SidebarContext): ReactElement,

  renderContent(context?: SidebarContext): ReactElement,
}

export enum WorkbenchTool {
  PAN = "pan",
  POINT = "point",
  ELLIPSE = "ellipse",
  RECTANGLE = "rectangle",
  POLYGON = "polygon",
  BRIGHTNESS = "brightness",
  CONTRAST = "contrast",
  SATURATION = "saturation",
  ROTATE = "rotate",
  RULER = "ruler",
  COMMENT = "comment",
}

export type WorkbenchToolMetadata = {
  text: string,
  iconType: typeof SvgIcon,
  disabled?: boolean,
  shape?: Shape,
  shapeFill?: string,
  shapeOpacity?: number,
  shapeStroke?: string,
  taskFilter?: (task: LabelerTask) => boolean,
  keyBinding?: string,
}

export const WORKBENCH_TOOL_KTM = new KeyMetadatas<WorkbenchToolMetadata>([
  $KTM(WorkbenchTool.PAN, {text: "Pan", iconType: PanToolOutlined, keyBinding: "H"}),
  $KTM(WorkbenchTool.POINT, {
    text: "Point",
    iconType: AdjustOutlined,
    shape: Shape.POINT,
    shapeFill: "#ff0",
    keyBinding: "V",
    taskFilter: task => task.category === LabelerTaskCategory.SEGMENTATION && task.segmentationTypes.includes(LabelerSegmentationType.POINT),
  }),
  $KTM(WorkbenchTool.ELLIPSE, {
    text: "Ellipse",
    iconType: CircleOutlined,
    shape: Shape.ELLIPSE,
    shapeFill: "#06f",
    shapeOpacity: 0.5,
    keyBinding: "E",
    taskFilter: task => task.category === LabelerTaskCategory.SEGMENTATION && task.segmentationTypes.includes(LabelerSegmentationType.ELLIPSE),
  }),
  $KTM(WorkbenchTool.RECTANGLE, {
    text: "Rectangle",
    iconType: RectangleOutlined,
    shape: Shape.RECTANGLE,
    shapeFill: "#06f",
    shapeOpacity: 0.5,
    keyBinding: "R",
    taskFilter: task => task.category === LabelerTaskCategory.SEGMENTATION && task.segmentationTypes.includes(LabelerSegmentationType.RECTANGLE),
  }),
  $KTM(WorkbenchTool.POLYGON, {
    text: "Polygon",
    iconType: ChangeHistoryOutlined,
    shape: Shape.POLYGON,
    shapeFill: "rgba(0, 64, 255, 0.25)",
    shapeOpacity: 0.5,
    keyBinding: "P",
    taskFilter: task => task.category === LabelerTaskCategory.SEGMENTATION && task.segmentationTypes.includes(LabelerSegmentationType.POLYGON),
  }),
  $KTM(WorkbenchTool.BRIGHTNESS, {text: "Brightness", iconType: BrightnessLowOutlined, keyBinding: "B"}),
  $KTM(WorkbenchTool.CONTRAST, {text: "Contrast", iconType: ContrastOutlined, keyBinding: "C"}),
  $KTM(WorkbenchTool.SATURATION, {text: "Saturation", iconType: GradientOutlined, keyBinding: "S"}),
  $KTM(WorkbenchTool.ROTATE, {text: "Rotate", iconType: RotateRightOutlined, keyBinding: "O"}),
  $KTM(WorkbenchTool.RULER, {text: "Ruler", iconType: StraightenOutlined, keyBinding: "M"}),
  $KTM(WorkbenchTool.COMMENT, {text: "Comment", iconType: InsertCommentOutlined, keyBinding: "N"}),
]);

export enum WorkbenchToggle {
  INVERT = "invert",
  ANNOTATIONS = "annotations",
  LABELS = "labels",
  POINT_SNAPPING = "point_snapping",
  CROSSHAIR = "crosshair",
}

export type WorkbenchToggleMetadata = {
  text: string,
  iconType: typeof SvgIcon,
}

export const WORKBENCH_TOGGLE_KTM = new KeyMetadatas<WorkbenchToggleMetadata>([
  $KTM(WorkbenchToggle.INVERT, {text: "Invert", iconType: InvertColorsOutlined}),
  $KTM(WorkbenchToggle.ANNOTATIONS, {text: "Annotations", iconType: BorderStyleOutlined}),
  $KTM(WorkbenchToggle.LABELS, {text: "Labels", iconType: LabelOutlined}),
  $KTM(WorkbenchToggle.POINT_SNAPPING, {text: "Point Snapping", iconType: CenterFocusStrongOutlined}),
  $KTM(WorkbenchToggle.CROSSHAIR, {text: "Crosshair", iconType: ControlCameraOutlined}),
]);

export type CanvasElementType = "image" | "shape" | "comment";

export type CanvasElement = {
  id: string;
  name: string;
  type: CanvasElementType;
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
  visible: boolean;
  locked: boolean;
}

export type ImageCanvasElement = CanvasElement & {
  type: "image";
  src: string;
  htmlImage: HTMLImageElement;
}

export type ShapeCanvasElement = CanvasElement & {
  type: "shape";
  shape: Shape;
  points?: number[];
  answerClass?: string;
}

export function canvasElementsToLabelerTaskLabels(elements: CanvasElement[]): LabelerTaskCaseLabel[] {
  const labels: LabelerTaskCaseLabel[] = [];
  if (!elements) {
    return labels;
  }
  for (let element of elements) {
    if (element.type === "shape") {
      const shapeEl = element as ShapeCanvasElement;
      switch (shapeEl.shape) {
        case Shape.POINT:
          labels.push(new LabelerTaskCaseLabel(
            [shapeEl.answerClass].filter(Boolean),
            new LabelerSegmentation(LabelerSegmentationType.POINT, [shapeEl.x, shapeEl.y]),
          ));
          break;
        case Shape.ELLIPSE:
          labels.push(new LabelerTaskCaseLabel(
            [shapeEl.answerClass].filter(Boolean),
            new LabelerSegmentation(LabelerSegmentationType.ELLIPSE, [shapeEl.x, shapeEl.y, shapeEl.width, shapeEl.height]),
          ));
          break;
        case Shape.RECTANGLE:
          labels.push(new LabelerTaskCaseLabel(
            [shapeEl.answerClass].filter(Boolean),
            new LabelerSegmentation(LabelerSegmentationType.RECTANGLE, [shapeEl.x, shapeEl.y, shapeEl.width, shapeEl.height]),
          ));
          break;
        case Shape.POLYGON:
          labels.push(new LabelerTaskCaseLabel(
            [shapeEl.answerClass].filter(Boolean),
            new LabelerSegmentation(LabelerSegmentationType.POLYGON, shapeEl.points),
          ));
          break;
      }
    }
  }
  return labels;
}

export function labelerTaskLabelsToCanvasElements(labels: LabelerTaskCaseLabel[]): ShapeCanvasElement[] {
  const elements: ShapeCanvasElement[] = [];
  if (!labels) {
    return elements;
  }
  for (let label of labels) {
    if (!label.segmentation) continue;
    const seg = label.segmentation;
    const answerClass = label.answerClasses?.[0];
    const base = {
      id: `shape_${Date.now()}`,
      type: "shape" as const,
      fill: "",
      rotation: 0,
      visible: true,
      locked: false,
      answerClass,
    };
    switch (seg.type) {
      case LabelerSegmentationType.POINT:
        elements.push({
          ...base,
          name: "Point",
          shape: Shape.POINT,
          x: seg.points[0],
          y: seg.points[1],
          width: 0,
          height: 0,
        });
        break;
      case LabelerSegmentationType.ELLIPSE:
        elements.push({
          ...base,
          name: "Ellipse",
          shape: Shape.ELLIPSE,
          x: seg.points[0],
          y: seg.points[1],
          width: seg.points[2],
          height: seg.points[3],
        });
        break;
      case LabelerSegmentationType.RECTANGLE:
        elements.push({
          ...base,
          name: "Rectangle",
          shape: Shape.RECTANGLE,
          x: seg.points[0],
          y: seg.points[1],
          width: seg.points[2],
          height: seg.points[3],
        });
        break;
      case LabelerSegmentationType.POLYGON:
        elements.push({
          ...base,
          name: "Polygon",
          shape: Shape.POLYGON,
          x: 0,
          y: 0,
          width: 0,
          height: 0,
          points: seg.points,
        });
        break;
    }
  }
  return elements;
}